import React, { useEffect, useState } from "react";
import { api, fmtCurrency, useAuth } from "../lib/api";
import { PageHeader } from "../components/layout/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Timesheets() {
  const { isAdmin } = useAuth();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(init());

  function init() { return { person_id: "", project_id: "", date: new Date().toISOString().slice(0, 10), hours: 8, description: "" }; }

  const load = async () => {
    // Use directory endpoints (no sensitive data) so non-admin roles can populate pickers.
    const requests = [
      api.get("/timesheets"),
      api.get("/directory/personnel"),
      api.get("/directory/projects"),
    ];
    if (isAdmin) requests.push(api.get("/timesheets/summary"));
    const results = await Promise.all(requests);
    setEntries(results[0].data);
    setPersonnel(results[1].data);
    setProjects(results[2].data);
    if (isAdmin) setSummary(results[3].data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const personMap = Object.fromEntries(personnel.map(p => [p.id, p]));
  const projectMap = Object.fromEntries(projects.map(p => [p.id, p]));

  const save = async () => {
    if (!form.person_id || !form.project_id) { toast.error("Person + project required"); return; }
    try { await api.post("/timesheets", form); toast.success("Logged"); setOpen(false); setForm(init()); load(); }
    catch { toast.error("Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete entry?")) return;
    try { await api.delete(`/timesheets/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div>
      <PageHeader
        overline="Delivery Cost"
        title="Timesheets"
        subtitle={isAdmin
          ? "Track billable hours by engineer / field tech against each project. Feeds actual delivery cost vs quoted price."
          : "Log the hours you worked on each project. You can only see entries you have submitted."}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-timesheet-btn" className="bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-mono uppercase tracking-wider text-xs"><Plus size={14} className="mr-2"/> Log Hours</Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-md">
              <DialogHeader><DialogTitle>Log Timesheet Entry</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Person</Label>
                  <Select value={form.person_id} onValueChange={(v) => setForm({...form, person_id: v})}>
                    <SelectTrigger data-testid="ts-person" className="bg-slate-50 border-slate-300 mt-1"><SelectValue placeholder="Select..."/></SelectTrigger>
                    <SelectContent>{personnel.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Project</Label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({...form, project_id: v})}>
                    <SelectTrigger data-testid="ts-project" className="bg-slate-50 border-slate-300 mt-1"><SelectValue placeholder="Select..."/></SelectTrigger>
                    <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
                  <div><Label>Hours</Label><Input data-testid="ts-hours" type="number" step="0.25" value={form.hours} onChange={(e) => setForm({...form, hours: parseFloat(e.target.value) || 0})} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
                </div>
                <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="bg-slate-50 border-slate-300 mt-1"/></div>
              </div>
              <DialogFooter><Button data-testid="save-timesheet-btn" onClick={save} className="bg-[#FFC107] hover:bg-[#E5A906]">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Summary — admin only */}
      {isAdmin && summary.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
          {summary.map((s) => (
            <div key={s.project_id} data-testid={`ts-summary-${s.project_id}`} className="border border-slate-200 bg-white rounded-lg p-4">
              <div className="text-sm font-medium text-[#0F1E38] truncate">{s.project_name}</div>
              <div className="mt-3 flex items-baseline justify-between">
                <div><div className="text-[10px] font-mono uppercase text-slate-500">Hours</div><div className="font-mono text-2xl text-[#0F1E38]">{s.total_hours.toFixed(1)}</div></div>
                <div className="text-right"><div className="text-[10px] font-mono uppercase text-slate-500">Cost</div><div className="font-mono text-lg text-emerald-600">{fmtCurrency(s.total_cost)}</div></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Entries */}
      <div className="border border-slate-200 bg-white rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left overline border-b border-slate-200">
              <th className="px-4 py-3">Date</th><th>Person</th><th>Project</th><th className="text-right">Hours</th><th>Description</th><th></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => {
              const person = personMap[e.person_id] || {};
              const project = projectMap[e.project_id] || {};
              return (
                <tr key={e.id} data-testid={`ts-row-${e.id}`} className="border-b border-slate-100">
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{e.date}</td>
                  <td className="text-[#0F1E38] text-xs">{person.name || "—"}</td>
                  <td className="text-slate-700 text-xs">{project.name || "—"}</td>
                  <td className="text-right font-mono text-[#0F1E38]">{e.hours}</td>
                  <td className="text-slate-500 text-xs">{e.description}</td>
                  <td className="pr-4 text-right"><button data-testid={`ts-del-${e.id}`} onClick={() => del(e.id)} className="text-slate-500 hover:text-red-400" style={{transition:"color 0.15s ease"}}><Trash size={13}/></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {entries.length === 0 && <div className="grid-bg py-16 text-center text-sm text-slate-500"><Clock size={24} className="mx-auto text-slate-400 mb-2" />No time logged yet.</div>}
      </div>
    </div>
  );
}
